// The state of one sale on the device (design section 11.6). Three independent parts:
// - local: whether the sale has been signed as an invoice. Signing is not implemented, so sales stay "saved".
// - sync: whether Delta has stored it. A sale is never deleted; "failed" waits for a person.
// - print: whether the signed invoice was printed. An unsigned sale is never printed as an invoice.

export type LocalState = "saved" | "signed";
export type SyncState = "pending" | "synced" | "failed";
export type PrintState = "not_printed" | "printed" | "copy_printed";

export interface SaleStatus { local: LocalState; sync: SyncState; print: PrintState }

export const INITIAL_STATUS: SaleStatus = Object.freeze({ local: "saved", sync: "pending", print: "not_printed" });

export type SaleEvent =
  | { type: "signed" }
  /** Delta acknowledged the upload (stored now or earlier). */
  | { type: "sync_ok" }
  /** Delta refused it for good (conflict or invalid); a person must look. */
  | { type: "sync_failed" }
  /** A manager sends a failed sale again. */
  | { type: "retry_requested" }
  | { type: "printed" };

export class InvalidTransition extends Error {
  constructor(readonly from: SaleStatus, readonly event: SaleEvent["type"]) {
    super(`cannot apply ${event} to a sale that is ${from.local}/${from.sync}/${from.print}`);
  }
}

/** The status after an event. Throws InvalidTransition for an event that is not allowed in the current status. */
export function nextStatus(s: SaleStatus, e: SaleEvent): SaleStatus {
  const bad = () => new InvalidTransition(s, e.type);
  switch (e.type) {
    case "signed":
      if (s.local !== "saved") throw bad();
      return { ...s, local: "signed" };
    case "sync_ok":
      // A repeated acknowledgement (retry after a lost response) changes nothing.
      if (s.sync === "failed") throw bad();
      return { ...s, sync: "synced" };
    case "sync_failed":
      if (s.sync !== "pending") throw bad();
      return { ...s, sync: "failed" };
    case "retry_requested":
      if (s.sync !== "failed") throw bad();
      return { ...s, sync: "pending" };
    case "printed":
      if (s.local !== "signed") throw bad();
      return { ...s, print: s.print === "not_printed" ? "printed" : "copy_printed" };
  }
}

/** The one word the cashier sees (design 11.6). */
export function displayStatus(s: SaleStatus): "Pending" | "Synced" | "Failed" {
  return s.sync === "synced" ? "Synced" : s.sync === "failed" ? "Failed" : "Pending";
}
